import React from 'react';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Container from 'components/Container';
import Heading from 'components/Heading';
import Row from 'components/Row';
import TextWithIcon from 'components/TextWithIcon';

import GetCoachingHomeDashboard from 'coaching/containers/GetCoachingHomeDashboard';
import CoachingHomeWelcomeHeader from 'coaching/CoachingHomeWelcomeHeader';
import CoachingSessionInfoContainer from 'coaching/CoachingSessionInfoContainer';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingView from 'coaching/components/CoachingView';

import {
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard,
} from 'coaching/queries/__generated__/GetCoachingHomeDashboard';
import ErrorReporter from 'app/components/ErrorReporter';
import CurrentUser from 'auth/CurrentUser';
import { getFirstNameLastInital } from 'coaching/lib/nameFormat';

/**
 * If _any_ of the student uuids for the participants do NOT equal the passed in UUID, then we're
 * looking at someone who's a parent.
 * This is a bit convoluted and would be better served with a "isParent" code
 */
function isParent(
  coachingHomeDashboard: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard
) {
  return (
    coachingHomeDashboard.participants.find(participant => {
      return participant.participantMetadata.studentUuid !== coachingHomeDashboard.uuid;
    }) !== undefined
  );
}

/**
 * Gathers all the individual sessions this user has.
 * If a participant has no sessions, return a session info container with an empty coaching session.
 * We also want to hide the free trial sessions after a user has completed a free trial session and has scheduled more
 * sessions.
 */
function getSessionsCards(
  coachingHomeDashboard: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard,
  participant: GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants
) {
  const sharedProps = {
    participantMetadata: participant.participantMetadata,
    userUuid: coachingHomeDashboard.uuid,
  };
  const hasPaidSessions = participant.coachingSessions.some(session => !session.isFreeTrial);
  const hasAtLeastOneUpcomingSession = participant.coachingSessions.some(session =>
    Boolean(session.nextInstanceStartDateTime)
  );
  return participant.coachingSessions.length === 0 || !hasAtLeastOneUpcomingSession
    ? [<CoachingSessionInfoContainer {...sharedProps} coachingSession={undefined} />]
    : participant.coachingSessions
        // if the user has any paid sessions, filter out the free trial
        .filter(session => (hasPaidSessions ? !session.isFreeTrial : true))
        // filter out any sessions that don't have an upcoming instance
        .filter(session => Boolean(session.nextInstanceStartDateTime))
        .map(coachingSession => {
          return (
            <CoachingSessionInfoContainer
              {...sharedProps}
              key={coachingSession.uuid}
              coachingSession={coachingSession}
            />
          );
        });
}

export default function CoachingHomeContainer() {
  return (
    <GetCoachingHomeDashboard>
      {({ coachingHomeDashboard, loading, error }) => {
        if (loading) {
          return <CenteredLoadingIndicator />;
        }
        if (!coachingHomeDashboard || error) {
          return <ErrorReporter error={`Could not load coaching home dashboard e=${error}`} />;
        }

        return (
          <CoachingView>
            <CoachingHomeWelcomeHeader firstName={CurrentUser.getUser().getFirstName()} />
            {coachingHomeDashboard?.participants.map(participant => {
              return (
                <div key={participant.uuid}>
                  <Row size="xsmall">
                    <Heading>
                      {getFirstNameLastInital(participant.participantMetadata.studentName)}
                    </Heading>
                  </Row>
                  {getSessionsCards(coachingHomeDashboard, participant)}
                </div>
              );
            })}
            {isParent(coachingHomeDashboard) && (
              <Button
                size="xxsmall"
                level="secondary"
                to={CoachingConstants.COACHING_PURCHASE_INTRO_ROUTE}>
                <TextWithIcon icon="plus-circle">Add a student</TextWithIcon>
              </Button>
            )}
          </CoachingView>
        );
      }}
    </GetCoachingHomeDashboard>
  );
}
